import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { accountsApi } from '../../api/accounts';
import type { AccountDto, CreateAccountRequest } from '../../types/account';
import DataTable from '../../components/DataTable';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useReportScreen } from '../../components/ScreenContext';

const CLASS_LABELS: Record<string, string> = {
  'LongTermAssets': '1 — Ilgalaikis turtas',
  'ShortTermAssets': '2 — Trumpalaikis turtas',
  'Equity': '3 — Nuosavas kapitalas',
  'Liabilities': '4 — Įsipareigojimai',
  'Income': '5 — Pajamos',
  'Expenses': '6 — Sąnaudos',
};

export default function AccountList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newAccount, setNewAccount] = useState<CreateAccountRequest>({
    accountNumber: 0,
    name: '',
    class: 'Income',
    saftCode: '',
  });

  const { data: accounts, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const createMutation = useMutation({
    mutationFn: accountsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setShowCreate(false);
      setNewAccount({ accountNumber: 0, name: '', class: 'Income', saftCode: '' });
    },
    onError: (err: Error) => alert(`Klaida: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: accountsApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
    onError: (err: Error) => alert(`Klaida: ${err.message}`),
  });

  // Group accounts by first digit
  const grouped = (accounts ?? []).reduce<Record<string, AccountDto[]>>((acc, a) => {
    const cls = String(a.accountNumber).charAt(0);
    if (!acc[cls]) acc[cls] = [];
    acc[cls].push(a);
    return acc;
  }, {});

  // Report screen context to AI copilot.
  // MUST be called before any early return (React hooks-order invariant,
  // otherwise React throws minified error #310 when isLoading flips).
  const accountCount = accounts?.length ?? 0;
  const accountPreview = useMemo(() =>
    Object.entries(grouped).flatMap(([, accs]) =>
      accs.slice(0, 2).map(a => `${a.accountNumber} ${a.name}`)
    ).slice(0, 10), [accounts]);

  useReportScreen(`Saskaitu planas (${accountCount} saskaitu)`, {
    recordCount: accountCount,
    preview: accountPreview,
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('accounts.title')}</h1>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100"
            onClick={() => navigate('/accounts/trial-balance')}
          >
            {t('accounts.trialBalance')}
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            onClick={() => setShowCreate(!showCreate)}
          >
            {t('accounts.newAccount')}
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-sm font-semibold mb-3">{t('accounts.newAccount').replace('+ ', '')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('accounts.accountNumber')}</label>
              <input
                type="number"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={newAccount.accountNumber || ''}
                onChange={(e) => setNewAccount({ ...newAccount, accountNumber: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.name')}</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={newAccount.name}
                onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('accounts.classLabel')}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={newAccount.class}
                onChange={(e) => setNewAccount({ ...newAccount, class: e.target.value })}
              >
                {Object.entries(CLASS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('accounts.saftCode')}</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={newAccount.saftCode}
                onChange={(e) => setNewAccount({ ...newAccount, saftCode: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              className="px-3 py-1.5 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
              onClick={() => setShowCreate(false)}
            >
              {t('common.cancel')}
            </button>
            <button
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
              onClick={() => createMutation.mutate(newAccount)}
              disabled={createMutation.isPending}
            >
              {t('common.create')}
            </button>
          </div>
        </div>
      )}

      {/* Account table by class */}
      {(['1', '2', '3', '4', '5', '6'] as const).map((cls) => {
        const classAccounts = grouped[cls];
        if (!classAccounts || classAccounts.length === 0) return null;

        return (
          <div key={cls} className="bg-white rounded-lg border border-gray-200">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-700">
                Klasė {cls} — {CLASS_LABELS[Object.entries(CLASS_LABELS).find(([_, v]) => v.startsWith(cls))?.[0] ?? 'Nežinoma']}
              </h2>
            </div>
            <DataTable
              columns={[
                { key: 'accountNumber', header: t('accounts.number'), className: 'w-20 num' },
                { key: 'name', header: t('common.name') },
                { key: 'class', header: t('accounts.classLabel') },
                { key: 'saftCode', header: t('accounts.saftCode') },
                {
                  key: 'actions', header: '', className: 'w-16 text-right',
                  render: (row) => (
                    <button
                      className="text-xs text-red-500 hover:text-red-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(t('accounts.deleteConfirm', { number: row.accountNumber }))) {
                          deleteMutation.mutate(row.accountNumber);
                        }
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  ),
                },
              ]}
              data={classAccounts}
              keyFn={(row) => row.accountNumber}
              emptyMessage=""
            />
          </div>
        );
      })}

      {(!accounts || accounts.length === 0) && (
        <div className="text-center py-8 text-gray-500">{t('accounts.noAccounts')}</div>
      )}
    </div>
  );
}
